(() => {
  'use strict';
  if (customElements.get('mc-reviews-block')) return;
  customElements.define('mc-reviews-block', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.more = this.querySelector('.mc-reviews-block__more');
      this.footer = this.querySelector('.mc-reviews-block__footer');
      this.navigation = this.querySelector('.mc-reviews-block__navigation');
      this.previous = this.querySelector('[data-mc-reviews-prev]');
      this.next = this.querySelector('[data-mc-reviews-next]');
      this.more?.addEventListener('click', () => {
        const expanded = this.toggleAttribute('data-expanded');
        this.more.setAttribute('aria-expanded', String(expanded));
        this.more.querySelector('span').textContent = expanded ? this.dataset.lessLabel : this.dataset.moreLabel;
        this.schedule();
      }, { signal });
      this.previous?.addEventListener('click', () => this.scrollCards(-1), { signal });
      this.next?.addEventListener('click', () => this.scrollCards(1), { signal });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this, { childList: true, subtree: true });
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.resizeObserver.observe(this);
      this.schedule();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    schedule() {
      if (this.frame || !this.isConnected) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }
    update() {
      const widget = this.querySelector('.jdgm-cards-carousel');
      const wrapper = widget?.querySelector('.jdgm-cards-wrapper');
      if (!widget || !wrapper) return;
      const cards = [...widget.querySelectorAll('.jdgm-card:not([data-clone-type])')]
        .sort((a, b) => Number(a.dataset.cardIndex) - Number(b.dataset.cardIndex));
      if (!cards.length) return;
      widget.querySelectorAll('.jdgm-card[data-clone-type] .jdgm-text[id]').forEach(text => text.removeAttribute('id'));
      widget.querySelectorAll('.jdgm-card[data-clone-type] .mc-reviews-block__readmore[aria-controls]').forEach(button => button.removeAttribute('aria-controls'));
      const grid = widget.querySelector('.jdgm-videos-container');
      if (grid && !grid.id) grid.id = `mc-reviews-grid-${this.dataset.instance}`;
      if (grid) this.more.setAttribute('aria-controls', grid.id);
      const ratingText = widget.querySelector('.jdgm-rating-text');
      const count = ratingText?.querySelector('.jdgm-reviews-count');
      if (count && !ratingText.querySelector('.mc-reviews-block__total')) {
        const value = ratingText.firstChild?.textContent?.match(/\d+[.,]\d+/)?.[0];
        if (value) {
          const total = document.createElement('span');
          total.className = 'mc-reviews-block__total';
          const parts = this.dataset.countLabel.split('__COUNT__');
          total.append(document.createTextNode(parts[0]), count, document.createTextNode(parts[1] || ''));
          const score = new Intl.NumberFormat(document.documentElement.lang || 'de', { maximumFractionDigits: 2 }).format(Number(value.replace(',', '.')));
          ratingText.replaceChildren(document.createTextNode(`${score} / 5`), total);
          widget.querySelector('.jdgm-header .jdgm-stars')?.setAttribute('aria-label', this.dataset.ratingLabel.replace('__RATING__', score));
        }
      }
      // Lead with different reviewers and concrete text that fits a compact card.
      // Never rank by stars; keep all original reviews and exclude slider clones.
      const names = new Set();
      const leading = [];
      const reviewerKey = card => {
        const name = card.querySelector('.jdgm-reviewer-name')?.textContent.trim().toLocaleLowerCase();
        return !name || /^(anonym|anonymous)$/.test(name) ? card.dataset.reviewId : name;
      };
      for (const card of cards) {
        const key = reviewerKey(card);
        if (leading.length >= 6 || names.has(key)) continue;
        names.add(key);
        const concrete = cards.find(candidate => {
          const length = candidate.querySelector('.jdgm-text')?.textContent.trim().length || 0;
          return reviewerKey(candidate) === key && length >= 45 && length <= 160;
        });
        leading.push(concrete || card);
      }
      const rest = cards.filter(card => !leading.includes(card));
      const ordered = [...leading, ...rest];
      const domOrder = [...widget.querySelectorAll('.jdgm-card:not([data-clone-type])')];
      // Keep keyboard and screen-reader order aligned with the visible cards.
      if (grid && domOrder.some((card, index) => card !== ordered[index])) grid.append(...ordered);
      ordered.forEach((card, index) => {
        card.style.order = String(index);
        card.toggleAttribute('data-mc-review-extra', index >= 6);
        const stars = card.querySelector('.jdgm-stars');
        if (stars && !stars.hasAttribute('role')) {
          const value = stars.querySelectorAll('.jdgm--on').length + stars.querySelectorAll('.jdgm--half').length * .5;
          stars.setAttribute('role', 'img');
          stars.setAttribute('aria-label', this.dataset.ratingLabel.replace('__RATING__', String(value)));
          stars.querySelectorAll('.jdgm-star').forEach(star => star.setAttribute('aria-hidden', 'true'));
        }
        const verified = card.querySelector('.jdgm-reviewer-verified-icon');
        if (verified && !verified.closest('.mc-reviews-block__verified')) {
          const label = document.createElement('span');
          label.className = 'mc-reviews-block__verified';
          verified.before(label);
          label.append(verified, document.createTextNode(this.dataset.verifiedLabel));
          verified.setAttribute('aria-hidden', 'true');
        }
        const product = card.querySelector('.jdgm-product-name a');
        if (product?.getAttribute('href') === '/' && product.textContent !== this.dataset.shopLabel) product.textContent = this.dataset.shopLabel;
        const text = card.querySelector('.jdgm-text');
        if (!text) return;
        if (!text.id) text.id = `mc-review-text-${this.dataset.instance}-${card.dataset.reviewId}`;
        let expand = card.querySelector('.mc-reviews-block__readmore');
        if (!expand) {
          expand = document.createElement('button');
          expand.type = 'button';
          expand.className = 'mc-reviews-block__readmore';
          expand.setAttribute('aria-expanded', 'false');
          expand.setAttribute('aria-controls', text.id);
          expand.textContent = this.dataset.readLabel;
          text.after(expand);
          expand.addEventListener('click', event => {
            event.stopPropagation();
            const expanded = card.classList.toggle('mc-review-expanded');
            expand.setAttribute('aria-expanded', String(expanded));
            expand.textContent = expanded ? this.dataset.closeLabel : this.dataset.readLabel;
            this.schedule();
          }, { signal: this.controller.signal });
        }
        const expanded = card.classList.contains('mc-review-expanded');
        expand.hidden = !expanded && text.scrollHeight <= text.clientHeight + 2;
      });
      this.more.hidden = ordered.length <= 6;
      this.footer.hidden = false;
      this.navigation.hidden = ordered.length <= 2;
      if (this.wrapper !== wrapper) {
        this.wrapper = wrapper;
        wrapper.addEventListener('scroll', () => this.syncNavigation(), { passive: true, signal: this.controller.signal });
      }
      this.syncNavigation();
    }
    syncNavigation() {
      if (!this.wrapper) return;
      this.previous.disabled = this.wrapper.scrollLeft <= 2;
      this.next.disabled = this.wrapper.scrollLeft + this.wrapper.clientWidth >= this.wrapper.scrollWidth - 2;
    }
    scrollCards(direction) {
      const card = this.wrapper?.querySelector('.jdgm-card:not([data-clone-type])');
      if (!card) return;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.wrapper.scrollBy({ left: direction * (card.getBoundingClientRect().width + 12), behavior: reduced ? 'auto' : 'smooth' });
    }
  });
})();
